/**
 * @fileoverview Componente responsável por renderizar a versão customizada de um item de ticket.
 */

import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey, red, blue } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Popover from "@material-ui/core/Popover";
import Grow from "@material-ui/core/Grow";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PersonIcon from '@material-ui/icons/Person';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import TicketMessagesDialog from "../TicketMessagesDialog";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import SyncHistoryModal from "../SyncHistoryModal";
import ConfirmationModal from "../ConfirmationModal";
import ReassignWhatsAppModal from "../ReassignWhatsAppModal";
import DoneIcon from '@material-ui/icons/Done';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { MailOpen, CheckCircle2, ArrowLeftRight, DownloadCloud, Trash2, Tag, NotebookPen, Pin, PinOff, Eye, Search, Check, X, ChevronDown, MapPin, Smartphone, Sparkles, Clock } from "lucide-react";
import InputBase from '@material-ui/core/InputBase';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import contrastColor from "../../helpers/contrastColor";
import ContactTag from "../ContactTag";
import ContactNotesDialog from "../ContactNotesDialog";
import AiStatusIndicator from "../AiStatusIndicator";
import { isTicketUnreplied } from "../../utils/isTicketUnreplied";

// Sentinela compartilhada pra evitar novo `[]` a cada render quando o ticket
// não tem tags — preserva referential equality e evita re-render desnecessário.
const EMPTY_TAGS = Object.freeze([]);

// Paleta padrão para criação de etiquetas no modal "Gerenciar Etiquetas".
// Evita que todas as tags novas saiam com a mesma cor default.
const TAG_PALETTE = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
];

// Prefixo gerado pelo upload quando o arquivo vem sem caption (ex: "📎 file-123.pdf").
const FILE_PREFIX_RE = /^📎\s*file-\d+/;
const MEDIA_EXT_PATTERNS = [
  { re: /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i, label: "📷 Imagem" },
  { re: /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i, label: "🎥 Vídeo" },
  { re: /\.(mp3|wav|ogg|aac|m4a|opus)$/i, label: "🎵 Áudio" },
  { re: /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt)$/i, label: "📄 Documento" },
];

const getMediaLabel = (message) => {
  if (!message) return message;
  if (message.includes("data:image/png;base64")) return "📍 Localização";
  if (!FILE_PREFIX_RE.test(message)) return message;
  for (const { re, label } of MEDIA_EXT_PATTERNS) if (re.test(message)) return label;
  return "📎 Arquivo";
};

// Formato: "data:image/png;base64,... | https://maps.google.com/... | LAT, LONG"
const extractLocationLink = (message) => {
  if (!message || !message.includes("data:image/png;base64") || !message.includes("maps.google.com")) return null;
  const parts = message.split("|");
  return parts.length >= 2 ? parts[1].trim() : null;
};

const TicketInfo = React.memo(({ ticket }) => {
  if (!ticket.chatbot) return null;
  return (
    <Tooltip title={i18n.t("ticketsListItem.tooltip.chatbot")}>
      <Smartphone size={18} style={{ color: grey[700], marginRight: 5 }} />
    </Tooltip>
  );
});

const useStyles = makeStyles((theme) => ({
  "@keyframes pinFlash": {
    "0%": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.28)"
          : "rgba(61, 92, 253, 0.18)",
    },
    "100%": { backgroundColor: "transparent" },
  },
  pinHighlight: {
    animation: "$pinFlash 700ms ease-out",
  },
  ticket: {
    position: "relative",
    borderBottom: "none !important",
    "&.MuiListItem-root": {
      borderBottom: "none !important",
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.06)",
      boxShadow: theme.palette.type === "dark"
        ? "inset 3px 0 0 0 #5B7CFF"
        : "inset 3px 0 0 0 #3D5CFD",
      "&:hover": {
        backgroundColor: theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.12)"
          : "rgba(0, 0, 0, 0.08)",
      },
    },
  },
  avatarContainer: {
    position: 'relative',
    width: 50,
    height: 50,
    marginRight: 12,
  },
  mainAvatar: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
  },
  attendantAvatar: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderRadius: '50%',
    bottom: -2,
    right: -2,
    border: `2px solid ${theme.palette.background.paper}`,
    fontSize: '0.7rem',
    backgroundColor: '#008000', // Verde como placeholder
    color: '#fff',
  },
  aiAttendantAvatar: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderRadius: '50%',
    bottom: -2,
    right: -2,
    border: `2px solid ${theme.palette.background.paper}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #5B7CFF 0%, #9D7CFF 100%)',
    color: '#fff',
  },
  pendingTicket: {
    cursor: "unset",
  },
  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: 'bold',
    paddingLeft: 5,
    paddingRight: 5,
    borderRadius: 3,
    fontSize: "0.8em",
    whiteSpace: "nowrap"
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  newMessagesCount: {
    marginRight: 8,
    marginLeft: 12,
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "transparent",
    color: (theme.palette.type === "light" ? "#000" : "#FFF"),
    marginRight: 1,
    padding: 0,
    fontWeight: 'bold',
    paddingLeft: 0,
    paddingRight: 0,
    borderRadius: 0,
    fontSize: "0.8em",
    whiteSpace: "nowrap",
    "& .MuiBadge-badge": {
      background: "transparent",
      color: (theme.palette.type === "light" ? "#000" : "#FFF"),
      boxShadow: "none",
      border: 0,
      padding: 0,
      minWidth: "auto",
      height: "auto",
      transform: "none",
      position: "relative",
    }
  },
  labelInline: {
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "0.72rem",
    marginRight: 8,
    color: (theme.palette.type === "light" ? "#000" : "#FFF"),
  },
  labelIcon: {
    fontSize: 14,
    opacity: 0.8,
    marginRight: 5,
    color: (theme.palette.type === "light" ? "#000" : "#FFF"),
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "5px",
    overflow: "hidden",
    width: "100%",
  },

  contactNameInner: {
    display: "inline-flex",
    alignItems: "center",
    minWidth: 0,
    flex: "0 1 auto",
    overflow: "hidden",
  },

  contactNameText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  unrepliedClockIcon: {
    color: red[600],
    marginLeft: 6,
    flexShrink: 0,
  },

  secondaryColumn: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",
    gap: 3,
    marginLeft: 5,
  },

  tagsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 2,
    alignItems: "center",
  },

  tagChipWrap: {
    position: "relative",
    display: "inline-flex",
    // Dentro do wrap o ContactTag dispensa o marginRight próprio (o gap do
    // tagsRow já separa) — assim o x encosta no canto da etiqueta.
    "& > div": { marginRight: 0 },
    "&:hover $tagRemoveBtn": { opacity: 1, transform: "scale(1)" },
  },
  tagRemoveBtn: {
    position: "absolute",
    top: -5,
    right: -5,
    width: 15,
    height: 15,
    padding: 0,
    margin: 0,
    borderRadius: "50%",
    border: "1.5px solid " + (theme.palette.type === "dark" ? "#1a1a1b" : "#fff"),
    background: "#ef4444",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    opacity: 0,
    transform: "scale(0.6)",
    transition: "opacity 0.12s ease, transform 0.12s ease",
    zIndex: 3,
    boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
    "& svg": { display: "block" },
    "&:hover": { background: "#dc2626" },
  },

  matchedMessageRow: {
    fontSize: "0.7rem",
    color: "#6366f1",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
  },

  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: 0
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: "85px",
    marginLeft: "5px",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    maxWidth: "calc(100% - 85px)",
  },


  badgeStyle: {
    color: "white",
    backgroundColor: "#6EC263",
    minWidth: "20px",
    height: "20px",
    borderRadius: "10px",
    padding: "0 6px",
    fontSize: "0.75rem"
  },

  actionButtonsContainer: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },

  acceptButton: {
    position: "absolute",
    right: "108px",
  },


  acceptButton: {
    position: "absolute",
    left: "50%",
  },


  ticketQueueColor: {
    flex: "none",
    width: "3px",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
    borderTopRightRadius: "4px",
    borderBottomRightRadius: "4px",
  },

  ticketInfo: {
    position: "relative",
    top: -13
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },

  },
  ticketActionButtons: {
    position: "absolute",
    right: "6px",
    top: "50%",
    transform: "translateY(-50%)",
    display: 'flex',
    alignItems: 'center',
  },
  // ── Tag Modal (padrão NewTicketModal) ──
  tagModalDialog: {
    "& .MuiDialog-paper": {
      fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
      width: "min(480px, calc(100vw - 32px))",
      maxWidth: "calc(100vw - 32px)",
      borderRadius: 24,
      overflow: "hidden",
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      "& *": {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
      },
    },
  },
  tagModalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2.5, 3),
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
  },
  tagModalTitle: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  tagModalClose: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  tagModalContent: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  tagModalSearchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.7, 1.8),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  tagModalSearchIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  tagModalSearchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    fontSize: "0.98rem",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  tagModalSelectedChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    minHeight: 0,
  },
  tagModalChip: {
    borderRadius: 999,
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#ffffff",
    textTransform: "uppercase",
    "& .MuiChip-deleteIcon": {
      color: "rgba(255, 255, 255, 0.7)",
      "&:hover": {
        color: "#ffffff",
      },
    },
  },
  tagModalList: {
    minHeight: 200,
    maxHeight: 320,
    overflowY: "auto",
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.02)"
        : "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
  },
  tagModalOption: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.2, 2),
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.04)"
          : "rgba(59, 130, 246, 0.04)",
    },
  },
  tagModalOptionSelected: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(59, 130, 246, 0.08)",
  },
  tagModalOptionLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  tagModalOptionDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    flexShrink: 0,
  },
  tagModalOptionLabel: {
    fontSize: "0.98rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  tagModalOptionCheck: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
  },
  tagModalCreateRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.2, 2),
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
    background: theme.palette.type === "dark"
      ? "rgba(91, 124, 255, 0.04)"
      : "rgba(91, 124, 255, 0.03)",
  },
  tagModalCreateLabel: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: "0 1 auto",
  },
  tagModalSwatchGroup: {
    display: "flex",
    gap: 4,
    marginLeft: "auto",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  tagModalSwatch: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "none",
    padding: 0,
    cursor: "pointer",
    transition: "transform 160ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 160ms",
    boxShadow: theme.palette.type === "dark"
      ? "inset 0 0 0 1px rgba(255,255,255,0.08)"
      : "inset 0 0 0 1px rgba(0,0,0,0.06)",
    "&:hover": {
      transform: "scale(1.18)",
      boxShadow: "0 0 0 2px rgba(91, 124, 255, 0.35)",
    },
    "&:focus-visible": {
      outline: "none",
      transform: "scale(1.18)",
      boxShadow: "0 0 0 2px rgba(91, 124, 255, 0.55)",
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  tagModalEmpty: {
    minHeight: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
    fontSize: "0.94rem",
  },
  tagModalActions: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2, 3, 2.5),
    borderTop:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
  },
  tagModalCloseBtn: {
    minWidth: 110,
    height: 36,
    borderRadius: 10,
    textTransform: "none",
    fontSize: "0.95rem",
    fontWeight: 500,
    boxShadow: "none",
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    "&:hover": {
      boxShadow: "none",
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
    },
  },

  // ======================================================================
  // Popover de ações do ticket — mesmo padrão do popover `+` do input
  // ======================================================================
  ticketMenuPaper: {
    borderRadius: 14,
    marginTop: 6,
    overflow: "visible",
    background: theme.palette.type === "dark" ? "#1c1f22" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: theme.palette.type === "dark"
      ? "0 20px 40px -12px rgba(0, 0, 0, 0.5), 0 6px 14px -4px rgba(0, 0, 0, 0.3)"
      : "0 20px 40px -12px rgba(15, 23, 42, 0.22), 0 6px 14px -4px rgba(15, 23, 42, 0.10)",
  },
  ticketMenu: {
    display: "flex",
    flexDirection: "column",
    padding: "6px 4px",
    minWidth: 232,
  },
  ticketMenuSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  ticketMenuEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    padding: "8px 12px 4px",
    color: theme.palette.type === "dark" ? "#6b7380" : "#94A3B8",
  },
  ticketMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    width: "100%",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#e6e8eb" : "#1a1a1a",
    transition: "background-color 140ms ease",
    animation: "$ticketMenuItemIn 200ms cubic-bezier(0.16, 1, 0.3, 1) both",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(15, 23, 42, 0.04)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD"}`,
      outlineOffset: 1,
    },
    "&:nth-child(1)": { animationDelay: "0ms" },
    "&:nth-child(2)": { animationDelay: "35ms" },
    "&:nth-child(3)": { animationDelay: "70ms" },
    "&:nth-child(4)": { animationDelay: "105ms" },
    "&:nth-child(5)": { animationDelay: "140ms" },
  },
  ticketMenuItemDanger: {
    color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(220, 38, 38, 0.12)"
        : "rgba(220, 38, 38, 0.06)",
    },
  },
  ticketMenuIconBox: {
    width: 28,
    height: 28,
    flex: "0 0 28px",
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
  },
  ticketMenuIconBoxCobalt: {
    background: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.16)" : "#EEF2FF",
    color: theme.palette.type === "dark" ? "#93a6ff" : "#3D5CFD",
  },
  ticketMenuIconBoxSlate: {
    background: theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.16)" : "#F1F5F9",
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#475569",
  },
  ticketMenuIconBoxAmber: {
    background: theme.palette.type === "dark" ? "rgba(252, 211, 77, 0.14)" : "#FEF3C7",
    color: theme.palette.type === "dark" ? "#fbbf24" : "#B45309",
  },
  ticketMenuIconBoxRed: {
    background: theme.palette.type === "dark" ? "rgba(248, 113, 113, 0.14)" : "#FEE2E2",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
  },
  ticketMenuLabel: {
    flex: 1,
    lineHeight: 1.2,
  },
  ticketMenuDivider: {
    height: 1,
    margin: "6px 8px",
    background: theme.palette.type === "dark"
      ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)"
      : "linear-gradient(90deg, transparent, #E2E8F0, transparent)",
  },
  "@keyframes ticketMenuItemIn": {
    "0%": { opacity: 0, transform: "translateY(4px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
}));
  {/*PLW DESIGN INSERIDO O dentro do const handleChangeTab*/}
  const TicketListItemCustom = ({ ticket, searchParam, now, showUnrepliedClockIcon = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const ticketUser = ticket?.userId && ticket?.user ? ticket.user?.name : null;

  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [syncHistoryModalOpen, setSyncHistoryModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [loadingTags, setLoadingTags] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [reassignWhatsAppModalOpen, setReassignWhatsAppModalOpen] = useState(false);
  const presence = ticket?.presence || null;
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { profile } = user;


  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCloseTicket = async (id) => {
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id,
        queueId: ticket?.queue?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null
      });
    } catch (err) {
      toastError(err);
    }
    history.push(`/tickets/`);
  };

  const handleReopenTicket = async (id) => {
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
        queueId: ticket?.queue?.id
      });
    } catch (err) {
      toastError(err);
    }
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleAcceptTicket = async (id) => {
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
    } catch (err) {
      toastError(err);
      return;
    }
    // Saudação automática (per-conexão em Whatsapps.greetingAcceptedEnabled)
    // é disparada pelo backend como efeito colateral do PUT.
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  // Funções do menu dropdown
  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsUnread = async () => {
    // Fecha o popover ANTES da API. Caso contrário, o ticket muda de estado
    // (unreadMessages / status) durante o exit do Grow e itens condicionais
    // re-renderizam dentro do popover já em fade-out, causando flicker.
    handleMenuClose();
    try {
      await api.post(`/tickets/${ticket.id}/unread`);
      // Fecha o painel (lado direito) limpando o ticket atual e navegando para /tickets
      setCurrentTicket({ id: null, code: null });
      history.push("/tickets");
    } catch (err) {
      toastError(err);
    }
  };

  const handleResolveTicket = async () => {
    handleMenuClose();
    await handleCloseTicket(ticket.id);
  };

  const handleTogglePin = async () => {
    handleMenuClose();
    try {
      await api.patch(`/tickets/${ticket.id}/pin`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTransferModal = () => {
    setTransferTicketModalOpen(true);
    handleMenuClose();
  };

  const handleCloseTransferTicketModal = () => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  };

  const handleOpenSyncHistoryModal = () => {
    setSyncHistoryModalOpen(true);
    handleMenuClose();
  };

  const handleCloseSyncHistoryModal = () => {
    if (isMounted.current) {
      setSyncHistoryModalOpen(false);
    }
  };

  const canSyncHistory =
    !ticket?.isGroup &&
    ticket?.whatsapp?.provider === "waha" &&
    !!ticket?.contact?.number;

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleMenuClose();
  };

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const [syncingTags, setSyncingTags] = useState(false);

  // Tags exibidas inline no card. Cópia local para remoção OTIMISTA (some na
  // hora do clique no x); a persistência vai via /tags/sync. Re-sincroniza
  // sempre que as tags do ticket mudarem (ex.: socket).
  const [displayTags, setDisplayTags] = useState(ticket?.tags || EMPTY_TAGS);
  useEffect(() => {
    setDisplayTags(ticket?.tags || EMPTY_TAGS);
  }, [ticket?.tags]);

  const handleOpenTagsModal = async () => {
    setTagsModalOpen(true);
    handleMenuClose();
    setTagSearch("");
    setLoadingTags(true);
    try {
      const { data } = await api.get("/tags/list");
      setAllTags(data);
      setSelectedTags(Array.isArray(ticket.tags) ? ticket.tags : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleCloseTagsModal = () => {
    setTagsModalOpen(false);
    setTagSearch("");
  };

  const syncTagsToServer = useCallback(async (ticketId, tags) => {
    setSyncingTags(true);
    try {
      await api.post("/tags/sync", { ticketId, tags });
    } catch (err) {
      toastError(err);
    } finally {
      setSyncingTags(false);
    }
  }, []);

  const handleToggleTag = useCallback(async (tagItem) => {
    if (syncingTags) return;
    const isSelected = selectedTags.some((t) => t.id === tagItem.id);
    const updated = isSelected
      ? selectedTags.filter((t) => t.id !== tagItem.id)
      : [...selectedTags, tagItem];
    setSelectedTags(updated);
    syncTagsToServer(ticket.id, updated);
  }, [syncingTags, selectedTags, ticket.id, syncTagsToServer]);

  const handleRemoveTag = useCallback(async (tagId) => {
    if (syncingTags) return;
    const updated = selectedTags.filter((t) => t.id !== tagId);
    setSelectedTags(updated);
    syncTagsToServer(ticket.id, updated);
  }, [syncingTags, selectedTags, ticket.id, syncTagsToServer]);

  // Remoção inline pelo "x" no canto da etiqueta (card da lista). Atualiza a UI
  // na hora (otimista) e persiste a lista resultante via /tags/sync.
  const handleRemoveTagInline = useCallback((e, tagId) => {
    e.stopPropagation();
    e.preventDefault();
    const updated = displayTags.filter((t) => t.id !== tagId);
    setDisplayTags(updated);
    setSelectedTags(updated);
    syncTagsToServer(ticket.id, updated);
  }, [displayTags, ticket.id, syncTagsToServer]);

  const filteredTags = useMemo(() => {
    if (!tagsModalOpen) return [];
    const search = tagSearch.toLowerCase();
    return search
      ? allTags.filter((t) => t.name.toLowerCase().includes(search))
      : allTags;
  }, [allTags, tagSearch, tagsModalOpen]);

  const canCreateTag = useMemo(() => {
    const trimmed = tagSearch.trim();
    return (
      trimmed.length >= 3 &&
      !allTags.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())
    );
  }, [tagSearch, allTags]);

  const handleCreateTag = useCallback(async (color) => {
    const name = tagSearch.trim();
    if (name.length < 3 || syncingTags) return;
    try {
      const { data: newTag } = await api.post("/tags", { name, color });
      setAllTags((prev) => [...prev, newTag]);
      const updated = [...selectedTags, newTag];
      setSelectedTags(updated);
      setTagSearch("");
      await syncTagsToServer(ticket.id, updated);
    } catch (err) {
      toastError(err);
    }
  }, [tagSearch, syncingTags, selectedTags, ticket.id, syncTagsToServer]);

  // Enter no input: não cria silenciosamente — usuário precisa escolher uma cor da paleta.
  const handleTagSearchKeyDown = useCallback((e) => {
    if (e.key === "Enter") e.preventDefault();
  }, []);

  const handleOpenNotesModal = () => {
    setNotesModalOpen(true);
    handleMenuClose();
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
  };

  // JSX memoizado pra ListItemText: evita criar uma árvore nova a cada render
  // do item virtualizado (re-render em scroll, atualização de presence, etc.).
  const primaryContent = useMemo(() => (
    <span className={classes.contactNameWrapper}>
      <span className={classes.contactNameInner}>
        <Typography
          noWrap
          component="span"
          variant="body2"
          color="textPrimary"
          className={classes.contactNameText}
        >
          {ticket.contact?.name || ticket.contact?.number || 'Contato'}
        </Typography>
        {showUnrepliedClockIcon && isTicketUnreplied(ticket, now) && (
          <Tooltip title={i18n.t("ticketsListItem.tooltip.unreplied")} arrow>
            <Clock
              size={14}
              strokeWidth={2.5}
              className={classes.unrepliedClockIcon}
            />
          </Tooltip>
        )}
      </span>
      <ListItemSecondaryAction>
        <div className={classes.ticketInfo1}><TicketInfo ticket={ticket} /></div>
      </ListItemSecondaryAction>
    </span>
  ), [ticket, showUnrepliedClockIcon, now, classes]);

  const secondaryContent = useMemo(() => (
    <span className={classes.secondaryColumn}>
      <Typography
        className={classes.contactLastMessage}
        noWrap
        component="span"
        variant="body2"
        color="textSecondary"
        style={ticket.status === "pending" ? {
          paddingRight: "140px",
          maxWidth: "calc(100% - 140px)",
        } : undefined}
      >
        {presence ? (
          <span style={{ color: green[500], fontStyle: "italic", fontWeight: 500 }}>
            {presence === "recording" ? i18n.t("ticketsListItem.recording") : i18n.t("ticketsListItem.typing")}
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <MarkdownWrapper>
              {getMediaLabel(ticket.lastMessage)}
            </MarkdownWrapper>
            {(() => {
              const locationLink = extractLocationLink(ticket.lastMessage);
              if (!locationLink) return null;
              return (
                <Tooltip title={i18n.t("ticketsListItem.viewLocation")}>
                  <IconButton
                    size="small"
                    disableRipple
                    disableTouchRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(locationLink, '_blank', 'noopener,noreferrer');
                    }}
                    style={{ padding: '2px', marginLeft: '4px' }}
                  >
                    <MapPin style={{ fontSize: '16px', color: '#d32f2f' }} />
                  </IconButton>
                </Tooltip>
              );
            })()}
          </span>
        )}
      </Typography>

      <AiStatusIndicator ticketId={ticket.id} />

      {ticket.matchedMessage && searchParam && (
        <span className={classes.matchedMessageRow}>
          <Search size={11} strokeWidth={1.75} style={{ verticalAlign: "middle", marginRight: 2, opacity: 0.7 }} />
          {(() => {
            const snippet = ticket.matchedMessage;
            const term = searchParam.toLowerCase();
            const idx = snippet.toLowerCase().indexOf(term);
            if (idx === -1) return snippet;
            return (
              <>
                {snippet.substring(0, idx)}
                <strong style={{ background: "rgba(99,102,241,0.2)", borderRadius: 2, padding: "0 1px" }}>
                  {snippet.substring(idx, idx + term.length)}
                </strong>
                {snippet.substring(idx + term.length)}
              </>
            );
          })()}
        </span>
      )}

      {displayTags?.length > 0 && (
        <span className={classes.tagsRow}>
          {displayTags.map((t) => (
            <span
              className={classes.tagChipWrap}
              key={`ticket-contact-tag-${ticket.id}-${t.id}`}
            >
              <ContactTag tag={t} />
              <button
                type="button"
                className={classes.tagRemoveBtn}
                onClick={(e) => handleRemoveTagInline(e, t.id)}
                onMouseDown={(e) => e.stopPropagation()}
                title="Remover etiqueta"
                aria-label={`Remover etiqueta ${t.name}`}
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </span>
      )}
    </span>
  ), [ticket, presence, searchParam, displayTags, handleRemoveTagInline, classes]);

  return (
    <React.Fragment key={ticket.id}>
      {openTicketMessageDialog && (
        <TicketMessagesDialog
          open={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticket.id}
        />
      )}
      {reassignWhatsAppModalOpen && (
        <ReassignWhatsAppModal
          open={reassignWhatsAppModalOpen}
          onClose={() => setReassignWhatsAppModalOpen(false)}
          ticketId={ticket.id}
          ticketStatus={ticket.status}
        />
      )}
      <ListItem dense button
        onClick={(e) => {
          // Se o clique veio do botão de menu ou de dentro do menu, não abre o chat
          const isMenuClick = e.target.closest && e.target.closest('[data-role="ticket-menu-button"], [data-role="ticket-menu"]');
          if (isMenuClick) return;
          if (ticket.status === "pending") return;
          handleSelectTicket(ticket);
        }}
        selected={!!ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.pinHighlight]:
            ticket._pinToggledAt &&
            Date.now() - ticket._pinToggledAt < 700,
        })}
      >
        <Tooltip arrow placement="right" title={ticket.queue?.name || i18n.t("ticketsListItem.noQueue")} >
          <span style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }} className={classes.ticketQueueColor}></span>
        </Tooltip>
        <ListItemAvatar>
          <div className={classes.avatarContainer}>
            <Avatar
              src={ticket?.contact?.profilePicUrl}
              className={classes.mainAvatar}
            />
            {/* Avatar do Agente IA nativo */}
            {ticket?.aiAgent && !ticketUser && (
              <Tooltip title={`Agente IA: ${ticket.aiAgent.name}`} arrow placement="top">
                {ticket.aiAgent.profilePicUrl ? (
                  <Avatar
                    src={ticket.aiAgent.profilePicUrl}
                    className={classes.aiAttendantAvatar}
                  />
                ) : (
                  <div className={classes.aiAttendantAvatar}>
                    <Sparkles size={14} />
                  </div>
                )}
              </Tooltip>
            )}
            {/* Avatar do usuário atendente */}
            {ticketUser && (
              <Tooltip title={ticket?.user?.isAI ? `IA: ${ticketUser}` : ticketUser} arrow placement="top">
                {ticket?.user?.isAI ? (
                  ticket?.user?.profileImage ? (
                    <Avatar
                      src={ticket?.user?.profileImage}
                      className={classes.aiAttendantAvatar}
                    />
                  ) : (
                    <div className={classes.aiAttendantAvatar}>
                      <Sparkles size={14} />
                    </div>
                  )
                ) : (
                  <Avatar
                    src={ticket?.user?.profileImage}
                    className={classes.attendantAvatar}
                  >
                    {!ticket?.user?.profileImage && ticketUser.charAt(0)}
                  </Avatar>
                )}
              </Tooltip>
            )}
          </div>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={primaryContent}
          secondary={secondaryContent}
        />
        <ListItemSecondaryAction>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center", gap: "3px" }}>
            {/* Container do horário */}
            {ticket.lastMessage && (() => {
              try {
                // Usa o MAX entre messages[0].createdAt e updatedAt. O socket de
                // appMessage recarrega o ticket SEM include de messages, então
                // `existing.messages` fica stale após nova mensagem. `updatedAt`
                // é bumpado pelo backend e reflete a atividade mais recente —
                // pegar o max corrige o display enquanto messages não atualiza.
                const msgCreated = ticket.messages?.[0]?.createdAt;
                const msgDate = msgCreated ? parseISO(msgCreated) : null;
                const updDate = ticket.updatedAt ? parseISO(ticket.updatedAt) : null;
                const msgTs = msgDate && !isNaN(msgDate.getTime()) ? msgDate.getTime() : 0;
                const updTs = updDate && !isNaN(updDate.getTime()) ? updDate.getTime() : 0;
                const latest = Math.max(msgTs, updTs);
                if (!latest) return null;
                const date = new Date(latest);

                return (
                  <Typography
                    className={classes.lastMessageTime}
                    component="span"
                    variant="body2"
                    color="textSecondary"
                    noWrap
                    style={{
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      minWidth: 'fit-content'
                    }}
                  >
                    {isSameDay(date, new Date()) ? (
                      <>{format(date, "HH:mm")}</>
                    ) : (
                      <>{format(date, "dd/MM")}</>
                    )}
                  </Typography>
                );
              } catch (error) {
                console.error('Erro ao formatar timestamp do ticket:', error);
                return null;
              }
            })()}
            
            {/* Container dos ícones */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
              {ticket.isPinned && (
                <Tooltip title="Conversa fixada">
                  <Pin
                    size={14}
                    strokeWidth={2}
                    style={{
                      color: grey[700],
                      transform: "rotate(45deg)"
                    }}
                  />
                </Tooltip>
              )}
              {/* Verifica se o ticket está órfão (não tem whatsappId) - não mostra para grupos */}
              {!ticket?.whatsappId && !ticket?.isGroup ? (
                <Tooltip title="Esta conversa não tem uma conexão. Clique para definir uma nova.">
                  <IconButton
                    size="small"
                    disableRipple
                    disableTouchRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      setReassignWhatsAppModalOpen(true);
                    }}
                    style={{ padding: 4 }}
                  >
                    <ErrorOutlineIcon
                      fontSize="small"
                      style={{ color: red[500] }}
                    />
                  </IconButton>
                </Tooltip>
              ) : ticket?.whatsapp?.name ? (
                <Tooltip title={ticket.whatsapp.name}>
                  <WhatsAppIcon
                    fontSize="small"
                    style={{ color: ticket.whatsapp.provider === 'meta-api' ? blue[400] : grey[700] }}
                  />
                </Tooltip>
              ) : null}
              {profile === "admin" && (
                <Tooltip title={i18n.t("ticketsListItem.tooltip.peek")}>
                  <Eye
                    onClick={() => setOpenTicketMessageDialog(true)} size={18}
                    style={{
                      color: grey[700],
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              )}
              {ticket.unreadMessages > 0 &&
                <Badge
                  className={classes.newMessagesCount}
                  badgeContent={ticket.unreadMessages}
                  classes={{
                    badge: classes.badgeStyle,
                  }}
                />
              }
              
              {/* Para tickets pendentes, mostrar botão direto de aceitar */}
              {ticket.status === "pending" ? (
                <Tooltip
                  title={ticket.chatbot && ticket.useIntegration && ticket.promptId
                    ? "Interromper IA e aceitar atendimento"
                    : "Aceitar conversa"
                  }
                  arrow
                  placement="left"
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAcceptTicket(ticket.id);
                    }}
                    style={{
                      backgroundColor: ticket.chatbot && ticket.useIntegration && ticket.promptId ? '#10B981' : '#3f51b5',
                      fontSize: '0.75rem',
                      padding: '4px 8px',
                      minWidth: 'auto',
                      textTransform: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#fff'
                    }}
                  >
                    {ticket.chatbot && ticket.useIntegration && ticket.promptId && (
                      <Smartphone style={{ fontSize: '1rem' }} />
                    )}
                    {ticket.chatbot && ticket.useIntegration && ticket.promptId ? "IA Ativa" : "Aceitar"}
                  </Button>
                </Tooltip>
              ) : (
                <IconButton
                  size="small"
                  disableRipple
                  disableTouchRipple
                  data-role="ticket-menu-button"
                  onClick={handleMenuClick}
                  style={{ padding: 2 }}
                  aria-label="Opções do ticket"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(anchorEl)}
                >
                  <ChevronDown size={16} style={{ color: grey[700] }}
                  />
                </IconButton>
              )}
            </div>
            
            {/* Popover de ações do ticket — command-menu agrupado */}
            <Popover
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              TransitionComponent={Grow}
              transitionDuration={{ enter: 200, exit: 140 }}
              PaperProps={{ className: classes.ticketMenuPaper }}
            >
              <div
                className={classes.ticketMenu}
                role="menu"
                data-role="ticket-menu"
              >
                {/* PENDING → apenas Aceitar conversa */}
                {ticket.status === "pending" && (
                  <div className={classes.ticketMenuSection}>
                    <div className={classes.ticketMenuEyebrow}>Ação</div>
                    <button
                      type="button"
                      role="menuitem"
                      className={classes.ticketMenuItem}
                      onClick={() => handleAcceptTicket(ticket.id)}
                    >
                      <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxCobalt}`}>
                        <CheckCircle2 size={16} strokeWidth={1.8} />
                      </span>
                      <span className={classes.ticketMenuLabel}>Aceitar conversa</span>
                    </button>
                  </div>
                )}

                {/* OPEN → 3 grupos + Deletar */}
                {ticket.status === "open" && (
                  <>
                    <div className={classes.ticketMenuSection}>
                      <div className={classes.ticketMenuEyebrow}>Status</div>
                      {!(ticket.unreadMessages > 0) && (
                        <button
                          type="button"
                          role="menuitem"
                          className={classes.ticketMenuItem}
                          onClick={handleMarkAsUnread}
                        >
                          <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxCobalt}`}>
                            <MailOpen size={16} strokeWidth={1.8} />
                          </span>
                          <span className={classes.ticketMenuLabel}>Marcar como não lida</span>
                        </button>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        className={classes.ticketMenuItem}
                        onClick={handleResolveTicket}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxCobalt}`}>
                          <CheckCircle2 size={16} strokeWidth={1.8} />
                        </span>
                        <span className={classes.ticketMenuLabel}>Resolver</span>
                      </button>
                    </div>

                    <div className={classes.ticketMenuDivider} />

                    <div className={classes.ticketMenuSection}>
                      <div className={classes.ticketMenuEyebrow}>Organizar</div>
                      <button
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={Boolean(ticket.isPinned)}
                        className={classes.ticketMenuItem}
                        onClick={handleTogglePin}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxSlate}`}>
                          {ticket.isPinned
                            ? <PinOff size={16} strokeWidth={1.8} />
                            : <Pin size={16} strokeWidth={1.8} />}
                        </span>
                        <span className={classes.ticketMenuLabel}>
                          {ticket.isPinned ? "Desafixar conversa" : "Fixar conversa"}
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={classes.ticketMenuItem}
                        onClick={handleOpenTagsModal}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxSlate}`}>
                          <Tag size={16} strokeWidth={1.8} />
                        </span>
                        <span className={classes.ticketMenuLabel}>Adicionar etiqueta</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={classes.ticketMenuItem}
                        onClick={handleOpenNotesModal}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxSlate}`}>
                          <NotebookPen size={16} strokeWidth={1.8} />
                        </span>
                        <span className={classes.ticketMenuLabel}>Adicionar anotação</span>
                      </button>
                    </div>

                    {!ticket?.isGroup && (
                      <>
                        <div className={classes.ticketMenuDivider} />
                        <div className={classes.ticketMenuSection}>
                          <div className={classes.ticketMenuEyebrow}>Movimentar</div>
                          {canSyncHistory && (
                            <button
                              type="button"
                              role="menuitem"
                              className={classes.ticketMenuItem}
                              onClick={handleOpenSyncHistoryModal}
                            >
                              <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxCobalt}`}>
                                <DownloadCloud size={16} strokeWidth={1.8} />
                              </span>
                              <span className={classes.ticketMenuLabel}>
                                {i18n.t("syncHistoryModal.menuItem")}
                              </span>
                            </button>
                          )}
                          <button
                            type="button"
                            role="menuitem"
                            className={classes.ticketMenuItem}
                            onClick={handleOpenTransferModal}
                          >
                            <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxAmber}`}>
                              <ArrowLeftRight size={16} strokeWidth={1.8} />
                            </span>
                            <span className={classes.ticketMenuLabel}>Transferir</span>
                          </button>
                        </div>
                      </>
                    )}

                    <div className={classes.ticketMenuDivider} />

                    <div className={classes.ticketMenuSection}>
                      <button
                        type="button"
                        role="menuitem"
                        className={`${classes.ticketMenuItem} ${classes.ticketMenuItemDanger}`}
                        onClick={handleOpenConfirmationModal}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxRed}`}>
                          <Trash2 size={16} strokeWidth={1.8} />
                        </span>
                        <span className={classes.ticketMenuLabel}>Deletar</span>
                      </button>
                    </div>
                  </>
                )}

                {/* CLOSED → só Etiqueta + Deletar */}
                {ticket.status === "closed" && (
                  <>
                    <div className={classes.ticketMenuSection}>
                      <div className={classes.ticketMenuEyebrow}>Organizar</div>
                      <button
                        type="button"
                        role="menuitem"
                        className={classes.ticketMenuItem}
                        onClick={handleOpenTagsModal}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxSlate}`}>
                          <Tag size={16} strokeWidth={1.8} />
                        </span>
                        <span className={classes.ticketMenuLabel}>Adicionar etiqueta</span>
                      </button>
                    </div>

                    <div className={classes.ticketMenuDivider} />

                    <div className={classes.ticketMenuSection}>
                      <button
                        type="button"
                        role="menuitem"
                        className={`${classes.ticketMenuItem} ${classes.ticketMenuItemDanger}`}
                        onClick={handleOpenConfirmationModal}
                      >
                        <span className={`${classes.ticketMenuIconBox} ${classes.ticketMenuIconBoxRed}`}>
                          <Trash2 size={16} strokeWidth={1.8} />
                        </span>
                        <span className={classes.ticketMenuLabel}>Deletar</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </Popover>
          </div>
        </ListItemSecondaryAction>
        
        {/* <span className={classes.secondaryContentSecond} >
          {ticket.status === "pending" && (
            <ButtonWithSpinner
              //color="primary"
              style={{ backgroundColor: '#3D5CFD', color: 'white', padding: '0px', bottom: '17px', borderRadius: '0px', left: '8px', fontSize: '0.6rem' }}
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
			  //PLW DESIGN INSERIDO O handleChangeTab
              onClick={e => handleAcceptTicket(ticket.id)}
            >
              {i18n.t("ticketsList.buttons.accept")}
            </ButtonWithSpinner>

          )}
          {(ticket.status !== "closed") && (
            <ButtonWithSpinner
              //color="primary"
              style={{ backgroundColor: '#E57373', color: 'white', padding: '0px', bottom: '0px', borderRadius: '0px', left: '8px', fontSize: '0.6rem' }}
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
              onClick={e => handleCloseTicket(ticket.id)}
            >
              {i18n.t("ticketsList.buttons.closed")}
            </ButtonWithSpinner>

          )}
          {(ticket.status === "closed") && (
            <ButtonWithSpinner
              //color="primary"
              style={{ backgroundColor: '#E57373', color: 'white', padding: '0px', bottom: '0px', borderRadius: '0px', left: '8px', fontSize: '0.6rem' }}
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
              onClick={e => handleReopenTicket(ticket.id)}
            >
              {i18n.t("ticketsList.buttons.reopen")}
            </ButtonWithSpinner>

          )}
        </span> */}
      </ListItem>

      {/* Modals - Movidos para fora do ListItem para evitar warnings do Material-UI */}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
        />
      )}

      {syncHistoryModalOpen && (
        <SyncHistoryModal
          open={syncHistoryModalOpen}
          ticketId={ticket.id}
          onClose={handleCloseSyncHistoryModal}
        />
      )}
      {confirmationOpen && (
        <ConfirmationModal
          title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
            ticket.id
          } ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
            ticket.contact?.name || ticket.contact?.number || 'Contato'
          }?`}
          open={confirmationOpen}
          onClose={setConfirmationOpen}
          onConfirm={handleDeleteTicket}
        >
          {i18n.t("ticketOptionsMenu.confirmationModal.message")}
        </ConfirmationModal>
      )}
      
      {/* Modal de Tags */}
      {tagsModalOpen && (
        <Dialog
          open={tagsModalOpen}
          onClose={handleCloseTagsModal}
          onClick={(e) => e.stopPropagation()}
          className={classes.tagModalDialog}
        >
          {/* Header */}
          <div className={classes.tagModalHeader}>
            <Typography className={classes.tagModalTitle}>
              Gerenciar Etiquetas
            </Typography>
            <IconButton size="small" onClick={handleCloseTagsModal} className={classes.tagModalClose}>
              <X size={18} strokeWidth={1.75} />
            </IconButton>
          </div>

          {/* Content */}
          <div className={classes.tagModalContent}>
            {/* Search */}
            <div className={classes.tagModalSearchField}>
              <Search className={classes.tagModalSearchIcon} size={16} strokeWidth={1.75} />
              <InputBase
                className={classes.tagModalSearchInput}
                placeholder="Buscar etiquetas..."
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                onKeyDown={handleTagSearchKeyDown}
                autoFocus
              />
            </div>

            {/* Selected chips */}
            {selectedTags.length > 0 && (
              <div className={classes.tagModalSelectedChips}>
                {selectedTags.map((t) => (
                  <Chip
                    key={t.id}
                    label={t.name.toUpperCase()}
                    size="small"
                    className={classes.tagModalChip}
                    style={{ backgroundColor: t.color || "#6b7280" }}
                    onDelete={() => handleRemoveTag(t.id)}
                  />
                ))}
              </div>
            )}

            {/* Tag list */}
            {loadingTags ? (
              <div className={classes.tagModalEmpty}>
                <CircularProgress size={28} />
              </div>
            ) : filteredTags.length === 0 && !canCreateTag ? (
              <div className={classes.tagModalEmpty}>
                {tagSearch ? "Nenhuma etiqueta encontrada" : "Nenhuma etiqueta disponível"}
              </div>
            ) : (
              <div className={classes.tagModalList}>
                {filteredTags.map((tagItem) => {
                  const isSelected = selectedTags.some((t) => t.id === tagItem.id);
                  return (
                    <div
                      key={tagItem.id}
                      className={`${classes.tagModalOption} ${isSelected ? classes.tagModalOptionSelected : ""}`}
                      onClick={() => handleToggleTag(tagItem)}
                    >
                      <div className={classes.tagModalOptionLeft}>
                        <div
                          className={classes.tagModalOptionDot}
                          style={{ backgroundColor: tagItem.color || "#6b7280" }}
                        />
                        <span className={classes.tagModalOptionLabel}>{tagItem.name}</span>
                      </div>
                      {isSelected && <Check className={classes.tagModalOptionCheck} size={16} strokeWidth={2} />}
                    </div>
                  );
                })}
                {canCreateTag && (
                  <div className={classes.tagModalCreateRow}>
                    <Tag size={16} strokeWidth={1.8} style={{ color: theme.palette.primary.main, flexShrink: 0 }} />
                    <span className={classes.tagModalCreateLabel}>
                      Criar "<strong>{tagSearch.trim()}</strong>"
                    </span>
                    <div className={classes.tagModalSwatchGroup}>
                      {TAG_PALETTE.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={classes.tagModalSwatch}
                          style={{ background: color }}
                          onClick={() => handleCreateTag(color)}
                          disabled={syncingTags}
                          aria-label={`Criar etiqueta com cor ${color}`}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={classes.tagModalActions}>
            <Button
              onClick={handleCloseTagsModal}
              variant="contained"
              className={classes.tagModalCloseBtn}
            >
              Fechar
            </Button>
          </div>
        </Dialog>
      )}
      
      {/* Modal de Anotações */}
      {notesModalOpen && (
        <ContactNotesDialog
          modalOpen={notesModalOpen}
          onClose={handleCloseNotesModal}
          ticket={ticket}
        />
      )}

      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default React.memo(TicketListItemCustom);
